import React, { useEffect, useMemo, useState } from 'react';
import {
  debounce,
  filter,
  flatMap,
  forEach,
  get,
  intersection,
  isEmpty,
  join,
  keys,
  map,
  max,
  min,
  omitBy,
  pick,
  reduce,
  size,
  sortBy,
  sumBy,
} from 'lodash';
import { useHistory, Link } from 'react-router-dom';
import moment from 'moment';
import {
  Button, ButtonBase, Dialog, DialogActions, DialogContent, Slide,
} from '@material-ui/core';
import { useMutation } from '@tanstack/react-query';
import calculatorIcon from '../images/calculator.png';
import useShoppingCart from '../contexts/shoppingCart';
import LocationGroup from '../components/shoppingCart/LocationGroup';
import { useAuthState } from '../../preorder/plugins/Auth';
import { checkShopCampaignDiscount } from '../api/menu';
import DiscountModal from '../components/shoppingCart/DiscountModal';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export const genGroupId = (item) => {
  const {
    provider = '',
    name = '',
    redeemStartTime = 0,
    fridgeId = '',
    onlineShopId = '',
    options,
  } = item || {};

  const optionsString = join(map(sortBy(keys(options)), (optionKey) => `${optionKey}=${options[optionKey]}`), '_');

  return btoa(encodeURIComponent(
    `${provider}_${name}_${redeemStartTime}_${fridgeId}_${onlineShopId}_${optionsString}`
  ));
};

export default () => {
  const { shoppingCart, removeItem, addItem } = useShoppingCart();
  const [isRemoveProductModalOpened, setIsRemoveProductModalOpened] = useState(false);
  const [isDiscountModalOpened, setIsDiscountModalOpened] = useState(false);
  const history = useHistory();
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const { token } = useAuthState();
  const {
    mutate, isLoading, data: shopCampaignDiscounts, reset,
  } = useMutation({
    async mutationFn(data) {
      const result = await checkShopCampaignDiscount({ token, ...data });

      return get(result, ['data'], []);
    },
  });

  const shopCampaignDiscountAmount = useMemo(() => sumBy(shopCampaignDiscounts, 'amount') || 0, [shopCampaignDiscounts]);

  const debounceMutate = useMemo(() => debounce(mutate, 300), [mutate]);

  const data = useMemo(() => {
    const result = {};

    forEach(sortBy(shoppingCart, 'id'), (item) => {
      const id = genGroupId(item);
      const {
        fridgeId,
        fridgeName,
        latestRegisterTime,
        locationPrompt,
      } = item || {};
      const disabled = moment().isAfter(latestRegisterTime);

      if (!result[fridgeId]) {
        result[fridgeId] = {
          id: fridgeId,
          name: fridgeName,
          description: locationPrompt,
          itemDict: {
          },
        };
      }

      if (!result[fridgeId].itemDict[id]) {
        result[fridgeId].itemDict[id] = {
          ...item,
          disabled,
          id,
          items: [],
        };
      }

      result[fridgeId].itemDict[id].items.push(item);
    });

    return result;
  }, [shoppingCart]);

  const selectedItems = useMemo(() => flatMap(
    pick(reduce(
      map(data, 'itemDict'),
      (p, n) => ({ ...p, ...n }),
      {}
    ), selectedItemIds),
    (i) => {
      const { items: iItems } = i || {};

      return iItems;
    }
  ), [data, selectedItemIds]);

  const originalPrice = useMemo(() => sumBy(selectedItems, 'price') || 0, [selectedItems]);

  const productDiscountAmount = useMemo(
    () => sumBy(selectedItems, (item) => {
      const { sellPrice, price } = item || {};

      return price - sellPrice;
    }) || 0,
    [selectedItems]
  );

  useEffect(() => {
    reset();

    if (isEmpty(selectedItems)) return;

    debounceMutate({ items: selectedItems });
  }, [selectedItems, reset, debounceMutate]);

  const onCheckoutButtonClick = () => {
    history.push(
      '/menu/checkout',
      {
        itemIds: selectedItemIds,
        items: selectedItems,
        shopCampaignDiscounts,
      }
    );
  };

  const onRemoveModalCancelButtonClick = () => {
    setIsRemoveProductModalOpened(false);
    setItemIdToDelete(null);
  };

  const onRemoveModalOkButtonClick = () => {
    if (itemIdToDelete) removeItem(get(reduce(map(data, 'itemDict'), (p, n) => ({ ...p, ...n }), {}), [itemIdToDelete, 'items']));
    setIsRemoveProductModalOpened(false);
    setItemIdToDelete(null);
    setSelectedItemIds(filter(selectedItemIds, (id) => id !== itemIdToDelete));
  };

  const onDiscountButtonClick = () => {
    setIsDiscountModalOpened(true);
  };

  const onDiscountModalOkButtonClick = () => {
    setIsDiscountModalOpened(false);
    onCheckoutButtonClick();
  };

  const onDiscountModalCancelButtonClick = () => {
    setIsDiscountModalOpened(false);
  };

  const onCheckboxChange = (e) => {
    const { checked, value } = e.target;

    if (checked) {
      setSelectedItemIds([...selectedItemIds, value]);
    } else {
      setSelectedItemIds(filter(selectedItemIds, (id) => id !== value));
    }
  };

  const onCheckAllCheckboxChange = (e) => {
    const { checked, value } = e.target;

    if (checked) {
      setSelectedItemIds(keys(omitBy(
        get(data, [value, 'itemDict'], {}),
        ['disabled', true]
      )));
    } else {
      setSelectedItemIds([]);
    }
  };

  return (
    isEmpty(data) ? (
      <div className="fz-bg-white fz-flex fz-flex-col fz-items-center fz-justify-center fz-h-full fz-text-lg">
        <span>您的餐籃內目前沒有任何商品</span>
        <Link to="/menu">來去逛逛</Link>
      </div>
    ) : (
      <div className="fz-container fz-m-auto fz-h-full fz-flex fz-flex-col">
        <Dialog
          open={isRemoveProductModalOpened}
          onClose={onRemoveModalCancelButtonClick}
          maxWidth="xs"
          fullWidth
          TransitionComponent={Transition}
        >
          <DialogContent className="fz-text-center">
            你確定要刪除嗎？
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={onRemoveModalCancelButtonClick}
              fullWidth
            >
              取消
            </Button>
            <Button
              className="!fz-text-white"
              color="secondary"
              fullWidth
              onClick={onRemoveModalOkButtonClick}
              variant="contained"
            >
              確定
            </Button>
          </DialogActions>
        </Dialog>
        <DiscountModal
          isOpened={isDiscountModalOpened}
          itemAmount={size(selectedItems)}
          onCancelButtonClick={onDiscountModalCancelButtonClick}
          onOkButtonClick={onDiscountModalOkButtonClick}
          originalPrice={originalPrice}
          productDiscountAmount={productDiscountAmount}
          shopCampaignDiscounts={shopCampaignDiscounts}
        />
        <div className="fz-flex fz-flex-col fz-gap-8 fz-bg-gray-200">
          { map(data, (location) => {
            const { id } = location || {};
            const disabled = !isEmpty(selectedItemIds)
              && isEmpty(intersection(selectedItemIds, keys(get(data, [id, 'itemDict']))));

            return (
              <div className="fz-bg-white" key={id}>
                <LocationGroup
                  readOnly={false}
                  addItem={addItem}
                  data={location}
                  onItemCheckboxChange={onCheckboxChange}
                  onLocationCheckboxChange={onCheckAllCheckboxChange}
                  removeItem={removeItem}
                  selectedItemIds={selectedItemIds}
                  setIsRemoveProductModalOpened={setIsRemoveProductModalOpened}
                  setItemIdToDelete={setItemIdToDelete}
                  disabled={disabled}
                  note={disabled && '一次僅能選擇同一個商店的商品進行結帳，請先結帳後再選擇其他商店的商品。'}
                />
              </div>
            );
          })}
        </div>
        <div className="fz-h-14 fz-sticky fz-mt-auto fz-items-center fz-bottom-0 fz-flex fz-bg-gray-100 fz-justify-end fz-gap-x-4 md:fz-static">
          {(!isEmpty(shopCampaignDiscounts) || productDiscountAmount !== 0) && (
          <div className="fz-flex fz-text-sm fz-flex-col">
            <div>
              <span>訂單優惠</span>
              <span className="fz-text-onSale fz-ml-2">{`$${min([shopCampaignDiscountAmount + productDiscountAmount, originalPrice])}`}</span>

            </div>
            <ButtonBase
              className="
                fz-flex
                fz-items-center
                !fz-border
                !fz-border-solid
                !fz-border-gray-400
                !fz-rounded
              "
              onClick={onDiscountButtonClick}
            >
              <span className="fz-bg-center fz-bg-contain fz-bg-no-repeat fz-w-4 fz-h-4" style={{ backgroundImage: `url(${calculatorIcon})` }} />
              <span>優惠試算</span>
            </ButtonBase>
          </div>
          )}
          <div className="fz-flex fz-gap-2 fz-font-bold">
            <span>
              總金額
            </span>
            <span className="fz-text-onSale">
              {`$${max([originalPrice - shopCampaignDiscountAmount - productDiscountAmount, 0])}`}
            </span>
          </div>
          <Button
            className="!fz-text-white fz-self-stretch fz-flex-shrink-0"
            color="secondary"
            disabled={isEmpty(selectedItemIds) || isLoading}
            onClick={onCheckoutButtonClick}
            variant="contained"
          >
            {`結帳 (${size(selectedItems)})`}
          </Button>
        </div>
      </div>
    )
  );
};
